import { useState } from 'react';
import { Modal, Table, Button, Spinner, Alert, Tab, Nav } from 'react-bootstrap';
import { getPremiumChangeTable, exportPremiumChangeTable } from '../../../../utils/api/insurancePolicy';
import globalErrorHandler from '../../../../utils/globalErrorHandler';
import LaddaButton, { EXPAND_LEFT } from 'react-ladda-button';
import { useTranslation } from 'react-i18next';
import { camelCaseToText } from '../../../../utils/camelCaseToText';
import { InsurancePolicyMainBranch } from '../../../../interfaces/InsurancePolicy';

interface PremiumChangeTableModalProps {
    show: boolean;
    onHide: () => void;
    policyId: string;
}

interface YearlyPremium {
    year: number;
    age: number;
    premium: number;
    premiumBeforeDiscount: number;
    discountPercentage: number;
}

interface PersonPremiumData {
    name: string;
    idNumber: string;
    currentAge: number;
    yearlyPremiums: YearlyPremium[];
}

interface PremiumChangeTableData {
    policyId: string;
    policyNumber?: string;
    company: string;
    branch: string;
    totalCoverage: number;
    persons: PersonPremiumData[];
}

const PremiumChangeTableModal = ({ show, onHide, policyId }: PremiumChangeTableModalProps) => {
    const { t } = useTranslation('insurance');
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [data, setData] = useState<PremiumChangeTableData | null>(null);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getPremiumChangeTable(policyId);
            setData(response);
        } catch (err: any) {
            setError(err.message || 'שגיאה בטעינת נתונים');
            globalErrorHandler(err);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            await exportPremiumChangeTable(policyId);
        } catch (err) {
            globalErrorHandler(err);
        } finally {
            setExporting(false);
        }
    };

    // Fetch data when modal opens
    const handleShow = () => {
        if (!data && !loading) {
            fetchData();
        }
    };

    const handleHide = () => {
        setData(null);
        setError(null);
        onHide();
    };

    return (
        <Modal show={show} onShow={handleShow} onHide={handleHide} dir="rtl" centered onClick={(e: any) => e.stopPropagation()}>
            <Modal.Header className="bg-info text-white" closeButton style={{ flexDirection: 'row-reverse' }}>
                <Modal.Title className="text-white">
                    {data && `${t('Premium Development')} - ${data.company} ביטוח ${t(camelCaseToText(data.branch))}`}
                    {data &&
                        !!data.totalCoverage &&
                        data.branch !== InsurancePolicyMainBranch.healthInsurance &&
                        data.totalCoverage > 0 &&
                        ` - ₪${data.totalCoverage.toLocaleString()}`}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {loading && (
                    <div className="text-center py-5">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">{t('Loading...')}</span>
                        </Spinner>
                        <p className="mt-3">{t('Loading data...')}</p>
                    </div>
                )}

                {error && !loading && (
                    <Alert variant="danger">
                        <strong>{t('Error')}:</strong> {error}
                    </Alert>
                )}

                {data && !loading && (
                    <div>
                        {data.persons.length === 0 ? (
                            <Alert variant="warning">{t('No data found for this policy')}</Alert>
                        ) : (
                            <Tab.Container defaultActiveKey="all">
                                {data.persons.length > 1 && (
                                    <Nav variant="tabs" className="mb-3">
                                        <Nav.Item>
                                            <Nav.Link eventKey="all">{t('All Insured')}</Nav.Link>
                                        </Nav.Item>
                                        {data.persons.map((person, personIndex) => (
                                            <Nav.Item key={personIndex}>
                                                <Nav.Link eventKey={`person-${personIndex}`}>{person.name}</Nav.Link>
                                            </Nav.Item>
                                        ))}
                                    </Nav>
                                )}
                                <Tab.Content>
                                    {/* Combined view - all persons */}
                                    <Tab.Pane eventKey="all">
                                        {/* Summary table with totals for all persons */}
                                        <div className="mb-5">
                                            <h5 className="mb-3 text-primary">
                                                {data.persons.length} {t('Insured Persons')}
                                            </h5>
                                            <Table striped bordered hover responsive>
                                                <thead className="table-primary">
                                                    <tr>
                                                        <th>{t('Year')}</th>
                                                        <th>{t('Total Premium Before Discount')}</th>
                                                        <th>{t('Total Monthly Premium')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        // Calculate combined totals per year
                                                        const yearTotalsMap = new Map<
                                                            number,
                                                            {
                                                                premiumBeforeDiscount: number;
                                                                premium: number;
                                                                discountPercentage: number;
                                                                count: number;
                                                            }
                                                        >();

                                                        // Aggregate data from all persons
                                                        data.persons.forEach((person) => {
                                                            person.yearlyPremiums.forEach((yearData) => {
                                                                const existing = yearTotalsMap.get(yearData.year);
                                                                if (existing) {
                                                                    existing.premiumBeforeDiscount += yearData.premiumBeforeDiscount;
                                                                    existing.premium += yearData.premium;
                                                                    existing.discountPercentage += yearData.discountPercentage;
                                                                    existing.count += 1;
                                                                } else {
                                                                    yearTotalsMap.set(yearData.year, {
                                                                        premiumBeforeDiscount: yearData.premiumBeforeDiscount,
                                                                        premium: yearData.premium,
                                                                        discountPercentage: yearData.discountPercentage,
                                                                        count: 1,
                                                                    });
                                                                }
                                                            });
                                                        });

                                                        // Convert to sorted array and render
                                                        return Array.from(yearTotalsMap.entries())
                                                            .sort((a, b) => a[0] - b[0])
                                                            .map(([year, totals]) => (
                                                                <tr key={year}>
                                                                    <td>
                                                                        <strong>{year}</strong>
                                                                    </td>
                                                                    <td>
                                                                        ₪
                                                                        {totals.premiumBeforeDiscount.toLocaleString(undefined, {
                                                                            minimumFractionDigits: 2,
                                                                            maximumFractionDigits: 2,
                                                                        })}
                                                                    </td>
                                                                    <td>
                                                                        <strong>
                                                                            ₪
                                                                            {totals.premium.toLocaleString(undefined, {
                                                                                minimumFractionDigits: 2,
                                                                                maximumFractionDigits: 2,
                                                                            })}
                                                                        </strong>
                                                                        {totals.discountPercentage > 0 && (
                                                                            <span className="text-success">
                                                                                {' '}
                                                                                ({(totals.discountPercentage / totals.count).toFixed(1)}%)
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ));
                                                    })()}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Tab.Pane>

                                    {/* Individual person tabs */}
                                    {data.persons.map((person, personIndex) => (
                                        <Tab.Pane eventKey={`person-${personIndex}`} key={personIndex}>
                                            <div className="mb-3">
                                                <h5>
                                                    {person.name} {t('ID')} {person.idNumber}
                                                </h5>
                                            </div>
                                            <Table striped bordered hover responsive>
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>{t('Year')}</th>
                                                        <th>{t('Age')}</th>
                                                        <th>{t('Monthly Premium')} (₪)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {person.yearlyPremiums.map((yearData, yearIndex) => (
                                                        <tr key={yearIndex}>
                                                            <td>{yearData.year}</td>
                                                            <td>{yearData.age}</td>
                                                            <td>
                                                                <strong>₪{yearData.premium.toLocaleString()}</strong>
                                                                {yearData.discountPercentage > 0 && (
                                                                    <span className="text-success"> ({yearData.discountPercentage}%)</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </Tab.Pane>
                                    ))}
                                </Tab.Content>
                            </Tab.Container>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer style={{ justifyContent: 'space-between' }}>
                <Button variant="secondary" onClick={handleHide}>
                    {t('Close')}
                </Button>
                {data && data.persons.length > 0 && (
                    <LaddaButton loading={exporting} data-style={EXPAND_LEFT} className="btn btn-success" onClick={handleExport}>
                        {exporting ? t('Exporting...') : t('Export to Excel')}
                    </LaddaButton>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default PremiumChangeTableModal;
